
import {Container, FederatedPointerEvent, Graphics} from 'pixi.js';
import DecodingModel from "../models/DecodingModel.ts";
import gsap from 'gsap';
import MarkingEvent from "@/events/MarkingEvent.ts";

export default class SyllableSlider extends Container {

    private BTN_RADIUS:number = 16;
    private SLIDER_WIDTH:number = 768;
    private V_LINE_HEIGHT:number = 178;
    private V_LINE_PADDING:number = 22;

    private circle:Graphics;
    private code:string;
    private correctVLine:Graphics;
    private curStepIndex:number;
    private dragContainer:Container;
    private dragTarget:Container;
    private firstKeyboardMove:boolean;
    private hLine:Graphics;
    private layer1:Container;
    private layer2:Container;
    private model:DecodingModel;
    private sliderPoints:number[];
    private sliderSteps:Graphics[];
    private vLine:Graphics;

    public get curPositionIndex():number
    {
        return this.curStepIndex+1;
    }

    constructor(code:string, sliderPoints:number[])
    {
        super();
        this.code = code;
        this.firstKeyboardMove = true;
        this.sliderPoints = sliderPoints
        this.model = DecodingModel.getInstance();
        this.curStepIndex = 0;
    }

    private addKeyboardListeners():void
    {
        document.addEventListener("keydown", this.onKeyUp);
    }

    private createCircleBtn():void
    {
        this.circle = new Graphics();
        this.circle.circle(0, 0, this.BTN_RADIUS);
        this.circle.fill({color:"#9550B4"});
        this.circle.stroke({width:4, color:"#FFFFFF"});
        this.dragContainer.addChild(this.circle);
        this.dragContainer.x = this.BTN_RADIUS/2;
        this.circle.eventMode = "static";
        this.circle.cursor = "pointer";
        this.circle.onpointerdown = this.onPointerDown;
    }

    private createDragContainer():void
    {
        this.dragContainer = new Container();
        this.layer2.addChild(this.dragContainer);
    }

    private createHorizontalLine():void
    {
        this.hLine = new Graphics();
        this.hLine.moveTo(0, 0);
        this.hLine.lineTo(this.SLIDER_WIDTH, 0);
        this.hLine.stroke({width:7, color:"#363538", cap:"round"});
        this.layer1.addChild(this.hLine);
    }

    private createLayers():void
    {
        this.layer1 = new Container();
        this.layer2 = new Container();
        this.addChild(this.layer1);
        this.addChild(this.layer2);
    }

    private createSliderSteps():void
    {
        this.sliderSteps = [];
        const len:number = this.sliderPoints.length;
        for(let i:number=0; i<len; i++)
        {
            const dot = new Graphics();
            dot.circle(0, 0, 4);
            dot.fill({color:"#ffffff"});
            dot.alpha = 0.95;
            dot.x = this.sliderPoints[i];
            this.layer1.addChild(dot);
            this.sliderSteps.push(dot);
        }
    }

    private createVerticalCorrectLine():void
    {
        this.correctVLine = new Graphics();
        this.correctVLine.moveTo(0, 0);
        this.correctVLine.lineTo(0, this.V_LINE_HEIGHT);
        this.correctVLine.stroke({width: 7, color: "#00E2FF", cap: "round"});
        this.correctVLine.pivot.set(0, this.correctVLine.height);
        this.correctVLine.x = 0;
        this.correctVLine.y = 0;
        this.correctVLine.visible = false;
        this.addChild(this.correctVLine);
    }

    private createVerticalLine():void
    {
        this.vLine = new Graphics();
        this.vLine.moveTo(0, 0);
        this.vLine.lineTo(0, this.V_LINE_HEIGHT);
        this.vLine.stroke({width: 7, color: "#363538", cap: "round"});
        this.vLine.pivot.set(0, this.vLine.height);
        this.vLine.x = this.circle.x;
        this.vLine.y = this.circle.y - this.BTN_RADIUS - this.V_LINE_PADDING;
        this.dragContainer.addChild(this.vLine);
    }

    private onKeyUp = (event:KeyboardEvent) =>
    {
        if(event.key == "ArrowRight")
        {
            if(this.curStepIndex < this.sliderSteps.length-1)
            {
                if(this.firstKeyboardMove)
                {
                    this.firstKeyboardMove = false;
                    gsap.to(this.dragContainer, {x:this.sliderSteps[this.curStepIndex].x, duration:0.75, ease: "elastic.out(1,0.3)"});
                    this.emit(MarkingEvent.SLIDER_STEP_SET);
                    return;
                }
                this.curStepIndex++;
                gsap.to(this.dragContainer, {x:this.sliderSteps[this.curStepIndex].x, duration:0.75, ease: "elastic.out(1,0.3)"});
                this.emit(MarkingEvent.SLIDER_STEP_SET);
            }
        }
        else if(event.key == "ArrowLeft")
        {
            if(this.curStepIndex > 0)
            {
                this.curStepIndex--;
                gsap.to(this.dragContainer, {x:this.sliderSteps[this.curStepIndex].x, duration:0.75, ease: "elastic.out(1,0.3)"});
                this.emit(MarkingEvent.SLIDER_STEP_SET);
            }
        }
    }

    private onPointerMove = (event:FederatedPointerEvent) =>
    {
        if(this.dragTarget)
        {
            const localPoint = this.toLocal(event.global);
            //console.log("localPoint.x: ", localPoint.x);
            //console.log("this.sliderSteps[0].x: ", this.sliderSteps[this.curStepIndex].x);
            const len:number = this.sliderSteps.length;
            for(let i:number=0; i<len; i++)
            {
                if(i == 0)
                {
                    if(localPoint.x <= this.sliderSteps[i].x)
                    {
                        this.curStepIndex = i;
                    }
                }
                else if(i == len-1)
                {
                    if(localPoint.x > this.sliderSteps[len-1].x)
                    {
                        this.curStepIndex = i;
                    }
                }
                else if(i > 0 && i < len-1)
                {
                    if(localPoint.x > this.sliderSteps[i].x && localPoint.x <= this.sliderSteps[i+1].x)
                    {
                        this.curStepIndex = i;
                    }
                }
            }
            //console.log("this.curStepIndex:", this.curStepIndex);
            if(localPoint.x >= this.BTN_RADIUS/2 && localPoint.x <= (this.SLIDER_WIDTH-this.BTN_RADIUS/2))
            {
                this.dragTarget.x = localPoint.x;
                const buffer:number = 20;
                const nextStepX:number = this.sliderSteps[this.curStepIndex].x;
                if(this.dragTarget.x >= nextStepX-buffer && this.dragTarget.x <= nextStepX+buffer)
                {
                    this.dragTarget.x = nextStepX;
                }
            }
        }
    }

    private onPointerDown = (event:FederatedPointerEvent) =>
    {
        this.dragTarget = this.dragContainer;
        this.model.app.stage.eventMode = 'static';
        this.model.app.stage.hitArea = this.model.app.screen;
        this.model.app.stage.on('pointerup', this.onPointerUp);
        this.model.app.stage.on('pointerupoutside', this.onPointerUpOutside);
        this.model.app.stage.on('pointermove', this.onPointerMove);
        this.emit(MarkingEvent.SLIDER_STEP_NOT_SET);
    }

    private onPointerUp = (event:FederatedPointerEvent) =>
    {
        this.resetStage();
        const buffer:number = 30;
        const nextStepX:number = this.sliderSteps[this.curStepIndex].x;
        if(this.dragTarget.x >= nextStepX-buffer && this.dragTarget.x <= nextStepX+buffer)
        {
            this.firstKeyboardMove = false;
            gsap.to(this.dragTarget, {x:nextStepX, duration:0.75, ease:"elastic.out(1,0.3)"});
            this.emit(MarkingEvent.SLIDER_STEP_SET);
        }
        if(this.dragTarget.x == nextStepX)
        {
            this.firstKeyboardMove = false;
            this.emit(MarkingEvent.SLIDER_STEP_SET);
        }
    }

    private onPointerUpOutside = (event:FederatedPointerEvent) =>
    {
        this.resetStage();
    }


    private resetStage():void
    {
        this.model.app.stage.eventMode = 'passive';
        this.model.app.stage.hitArea = null;
        //this.model.app.stage.on('pointerup', null);
        //this.model.app.stage.on('pointerupoutside', null);
        //this.model.app.stage.on('pointermove', null);
    }


    public create():void
    {
        this.createLayers();
        this.createHorizontalLine();
        this.createDragContainer();
        this.createVerticalCorrectLine();
        this.createCircleBtn();
        this.createVerticalLine();
        this.createSliderSteps();
        this.addKeyboardListeners();
        this.pivot.x = this.SLIDER_WIDTH/2;
    }

    public moveToCorrectPosition(position:number):void
    {
        this.curStepIndex = position-1;
        gsap.to(this.dragContainer, {x:this.sliderSteps[position-1].x, duration:0.75, ease: "elastic.out(1,0.3)"});
        gsap.to(this.correctVLine, {alpha:0, duration:0.5, onComplete:()=>{this.correctVLine.visible = false;}});
    }

    public showCorrectLine(position:number):void
    {
        this.correctVLine.x = this.sliderSteps[position-1].x;
        this.correctVLine.y = this.circle.y - this.BTN_RADIUS - this.V_LINE_PADDING;
        this.correctVLine.alpha = 1;
        this.correctVLine.visible = true;
    }
}